import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Container, Alert, Button, Spinner } from 'react-bootstrap';
import api from '../http-common';

const EmailVerificationPage = () => {
  const { 
    isAuthenticated, 
    isLoading: authLoading,
    user,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
  } = useAuth0();

  const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [lastCheckedVerified, setLastCheckedVerified] = useState(null);
  const [requiresConsent, setRequiresConsent] = useState(false);

  const initialCheckDoneRef = useRef(false);
  const refreshInFlightRef = useRef(false);
  const lastRefreshAtRef = useRef(0);
  const autoReloginAttemptedRef = useRef(false);

  const refreshSessionBestEffort = async (opts = {}) => {
    const { force = false } = opts;
    const now = Date.now();
    if (refreshInFlightRef.current) return;
    // Évite les rafraîchissements agressifs (et donc /oauth/token en boucle)
    if (!force && now - lastRefreshAtRef.current < 15000) return;
    refreshInFlightRef.current = true;
    lastRefreshAtRef.current = now;
    let consentRequired = false;
    try {
      if (force) {
        await getAccessTokenSilently({ cacheMode: 'off' });
      } else {
        await getAccessTokenSilently();
      }
    } catch (e) {
      const errCode = e?.error || e?.code;
      if (errCode === 'consent_required') {
        consentRequired = true;
        setError("Consentement requis par Auth0. Une redirection va s'ouvrir pour autoriser l'accès.");
        setRequiresConsent(true);
        return { consentRequired };
      }
      console.warn('[verify-email] Silent token refresh failed, will still check ID token claims:', e);
    } finally {
      refreshInFlightRef.current = false;
    }
    return { consentRequired };
  };

  // Vérification initiale
  useEffect(() => {
    const checkAuth = async () => {
      if (!authLoading && !isAuthenticated) {
        // Ne pas rediriger automatiquement
        console.log('Utilisateur non authentifié, mais pas de redirection automatique');
        return;
      }

      if (isAuthenticated) {
        try {
          if (initialCheckDoneRef.current) return;
          initialCheckDoneRef.current = true;
          await refreshSessionBestEffort();
          const claims = await getIdTokenClaims({ ignoreCache: true });
          if (claims?.email_verified) {
            window.location.href = '/';
          }
        } catch (error) {
          console.error('Erreur lors de la vérification:', error);
        }
      }
    };

    checkAuth();
  }, [isAuthenticated, authLoading, getAccessTokenSilently, getIdTokenClaims]);

  const checkEmailVerification = async () => {
    setIsLoading(true);
    setError(null);
    setEmailSent(false);
    setRequiresConsent(false);
    autoReloginAttemptedRef.current = false;

    try {
      const refreshRes = await refreshSessionBestEffort({ force: true });

      if (refreshRes?.consentRequired && !autoReloginAttemptedRef.current) {
        autoReloginAttemptedRef.current = true;
        await forceReloginToRefreshClaims();
        return;
      }

      try {
        if (auth0Domain) {
          const token = await getAccessTokenSilently();
          const resp = await fetch(`https://${auth0Domain}/userinfo`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (resp.ok) {
            const profile = await resp.json();
            if (profile?.email_verified === true) {
              window.location.href = '/';
              return;
            }
          }
        }
      } catch (_) {}

      const claims = await getIdTokenClaims({ ignoreCache: true });

      try {
        console.log('[verify-email] claims:', {
          email: claims?.email,
          email_verified: claims?.email_verified,
          sub: claims?.sub,
        });
      } catch (_) {}
      
      setLastCheckedVerified(typeof claims?.email_verified === 'boolean' ? claims.email_verified : null);
      
      if (claims?.email_verified) {
        window.location.href = '/';
        return;
      } else {
        if (!autoReloginAttemptedRef.current) {
          autoReloginAttemptedRef.current = true;
          await forceReloginToRefreshClaims();
          return;
        }
        setError('Email non vérifié. Vérifiez votre boîte mail et cliquez sur le lien de vérification.');
      }
    } catch (error) {
      console.error('Erreur lors de la vérification:', error);

      const errCode = error?.error || error?.code;
      const errMsg = error?.message || String(error);
      setError(`Erreur lors de la vérification. (${errCode || 'unknown_error'}) ${errMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const forceReloginToRefreshClaims = async () => {
    try {
      const returnTo = '/verify-email';
      const authorizationParams = requiresConsent ? { prompt: 'consent' } : {};
      await loginWithRedirect({
        appState: { returnTo },
        authorizationParams,
      });
    } catch (e) {
      const errMsg = e?.message || String(e);
      setError(`Impossible de relancer l'authentification. ${errMsg}`);
    }
  };

  const handleResendVerification = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setEmailSent(false);

      const token = await getAccessTokenSilently();

      const response = await api.post(
        '/resend-verification',
        { email: user?.email },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response || response.status < 200 || response.status >= 300) {
        throw new Error("Échec de l'envoi");
      }
      setEmailSent(true);
    } catch (error) {
      const details = error?.response?.data?.details || error?.response?.data?.error;
      const errMsg = details || error?.message || String(error);
      setError(`Erreur lors de l'envoi de l'email. ${errMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Si l'authentification est en cours de chargement
  if (authLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" />
      </div>
    );
  }

  // Si l'utilisateur n'est pas authentifié, afficher un message
  if (!isAuthenticated) {
    console.log('Non authentifié - Détails:', {
      isAuthenticated,
      authLoading,
      user,
      location: window.location.href,
      hasToken: !!localStorage.getItem('@@auth0spajs@@::default::default::openid profile email')
    });
    return (
      <Container className="mt-5 text-center">
        <Alert variant="warning">
          <p>Vous devez être connecté pour accéder à cette page.</p>
          <Button 
            variant="primary" 
            onClick={() => window.location.href = '/'}
          >
            Retour à l'accueil
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-5" style={{ maxWidth: '600px' }}>
      <Alert variant="info" className="shadow">
        <Alert.Heading>Vérification d'email requise</Alert.Heading>
        
        <p className="my-3">
          Un email de vérification a été envoyé à <strong>{user?.email}</strong>.
          Veuillez vérifier votre boîte de réception et cliquer sur le lien de vérification.
        </p>

        {emailSent && <Alert variant="success">Email envoyé avec succès !</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        {lastCheckedVerified !== null && (
          <Alert variant={lastCheckedVerified ? 'success' : 'warning'}>
            Statut actuel (token): {lastCheckedVerified ? 'Email vérifié' : 'Email non vérifié'}
          </Alert>
        )}

        <div className="d-flex gap-3 mt-4">
          <Button 
            variant="primary" 
            onClick={checkEmailVerification}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Vérification...
              </>
            ) : (
              'J\'ai vérifié mon email'
            )}
          </Button>
          
          <Button 
            variant="outline-secondary" 
            onClick={handleResendVerification}
            disabled={isLoading}
          >
            Renvoyer l'email
          </Button>
        </div>

        <div className="mt-3 text-muted small">
          <p>Vous ne voyez pas l'email ?</p>
          <ul>
            <li>Vérifiez votre dossier de courrier indésirable</li>
            <li>Assurez-vous que l'email provient de {window.location.host}</li>
          </ul>
        </div>
      </Alert>
    </Container>
  );
};

export default EmailVerificationPage;